import {
  Component,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { TextFieldProps } from "../f-text-field/f-text-field-model";

@Component({
  tag: "f-value-modificator",
  styleUrl: "f-value-modificator.scss",
  shadow: true,
})
export class ValueModificator {
  @Prop() propertyId: TextFieldProps["propertyId"];
  @Prop() name: TextFieldProps["name"];
  @Prop() label: TextFieldProps["label"];
  @Prop() min: TextFieldProps["min"] = 1;
  @Prop() max: TextFieldProps["max"] = 999999;
  @Prop() step: TextFieldProps["step"] = 1;
  @Prop() defaultValue: TextFieldProps["min"] = this.min;
  @Prop() isValid?: () => boolean;
  @Prop({ mutable: true }) disabled? = false;
  @Prop() numberOfDigits: number = this.max;
  @Prop() inputQuantityTitle: string;
  @Prop() increaseQuantityTitle: string;
  @Prop() decreaseQuantityTitle: string;
  @Prop() quantity: number;

  @State() value;
  @State() previousValue;

  private numberInput?: HTMLInputElement;

  @Watch("quantity")
  quantityHandler(): void {
    this.value = this.quantity;
  }

  componentWillLoad(): void {
    if (!ValueModificator.isProvided(this.defaultValue)) {
      this.value = this.min;
    }

    this.value = Math.min(Math.max(this.defaultValue, this.min), this.max);
  }

  // prettier-ignore
  @Event({ eventName: "valueModificatorChanged" }) valueModificatorChanged: EventEmitter<{
    fieldId: string;
    oldValue: number;
    newValue: number;
    valid: boolean;
    showErrorMessage: boolean;
  }>;

  onValueChange() {
    const previousValue = this.previousValue;
    let showErrorMessage = false;

    if (this.disabled) {
      return;
    }

    if (this.value > this.max) {
      this.value = this.max;

      showErrorMessage = true;
    }

    if (this.value < this.min) {
      this.value = this.min;

      showErrorMessage = true;
    }

    if ((this.value - this.min) % this.step !== 0) {
      this.value =
        this.value + this.step - ((this.value - this.min) % this.step);

      showErrorMessage = true;
    }

    if (showErrorMessage) {
      this.previousValue = this.value;
      this.numberInput.value = String(this.value);
    }

    this.valueModificatorChanged.emit({
      fieldId: this.propertyId,
      oldValue: previousValue,
      newValue: this.value,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
      showErrorMessage,
    });
  }

  private handleChange = (): void => {
    if (!this.disabled) {
      this.previousValue = this.value;
      const newValue = Number(this.numberInput.value);
      this.value = newValue;

      this.onValueChange();
    }
  };

  private incrementValue = (): void => {
    if (!this.disabled) {
      this.previousValue = this.value;
      this.value = this.value + this.step;
      this.onValueChange();
    }
  };

  private decrementValue = (): void => {
    if (!this.disabled) {
      this.previousValue = this.value;
      this.value = this.value - this.step;
      this.onValueChange();
    }
  };

  private static isProvided(value: number): boolean {
    return value !== null && value !== undefined;
  }

  render() {
    return (
      <f-text-field-group
        disabled={this.disabled}
        data-test="value-modificator-field-group"
      >
        <f-text-field
          type="number"
          name={this.name}
          min={this.min}
          max={this.max}
          step={this.step}
          value={this.value}
          propertyId={this.propertyId}
          disabled={this.disabled}
          inputChangeHandler={this.handleChange}
          ref={(el) => (this.numberInput = el as unknown as HTMLInputElement)}
          label={this.label}
          maxLength={this.numberOfDigits}
          title={this.inputQuantityTitle}
          data-test="value-modificator-text-field"
        />

        <div
          class={{
            "a-text-field-group__button": true,
            "a-text-field-group--not-allowed": this.value <= this.min,
          }}
        >
          <f-button
            type="button"
            variant="integrated"
            iconOnly={true}
            disabled={this.value <= this.min || this.disabled}
            onClick={this.decrementValue}
            title={this.decreaseQuantityTitle}
            data-test="value-modificator-button-less"
          >
            <f-icon
              iconName="less-minimize"
              data-test="value-modificator-icon-less"
            />
            <f-visually-hidden>{this.decreaseQuantityTitle}</f-visually-hidden>
          </f-button>
        </div>

        <div
          class={{
            "a-text-field-group__button": true,
            "a-text-field-group--not-allowed": this.value >= this.max,
          }}
        >
          <f-button
            type="button"
            variant="integrated"
            iconOnly={true}
            disabled={this.value >= this.max || this.disabled}
            onClick={this.incrementValue}
            title={this.increaseQuantityTitle}
            data-test="value-modificator-button-add"
          >
            <f-icon iconName="add" data-test="value-modificator-icon-add" />
            <f-visually-hidden>{this.increaseQuantityTitle}</f-visually-hidden>
          </f-button>
        </div>
      </f-text-field-group>
    );
  }
}
